import { message } from 'antd';

import { copyToClipboard as safeCopyToClipboard } from '../../../../../../helpers/utils/safeClipboard';

interface MetricItem {
    dataKey?: string;
    label?: string;
    [key: string]: any;
}

interface GroupingItem {
    label?: string;
    [key: string]: any;
}

interface TableDataItem {
    group?: string;
    pathStr?: string;
    children?: TableDataItem[];
    clientData?: any;
    [key: string]: any;
}

/**
 * Удаляет HTML-теги и &nbsp; из строки
 */
const removeNbsp = (value: string): string => {
    return value.replace(/&nbsp;/g, ' ').replace(/<[^>]*>/g, '');
};

/**
 * Извлекает текст из HTML-ссылки
 */
const extractLinkText = (value: string): string => {
    if (`${value}`.includes('<a')) {
        const parts = `${value}`.split('>');
        const innerParts = parts[1]?.split('<') || [];
        return innerParts[0] || '';
    }
    return value;
};

/**
 * Парсит строку данных таблицы рекурсивно
 */
const parseRow = (
    item: TableDataItem,
    metrics: MetricItem[],
    prevTitle?: string
): any[][] => {
    const row: any[] = [];

    Object.entries(item).forEach(([key, value]: [string, any]) => {
        if (key === 'group') {
            const extractedValue = extractLinkText(value);
            row[0] = prevTitle 
                ? `${prevTitle} - ${removeNbsp(extractedValue)}` 
                : removeNbsp(extractedValue);
        }

        if (
            key !== 'id' &&
            key !== 'pathStr' &&
            key !== 'children' &&
            key !== 'clientData'
        ) {
            metrics.forEach((metricItem, index) => {
                if (metricItem.dataKey && key === metricItem.dataKey) {
                    const extractedValue = extractLinkText(value);
                    row[index + 1] = `${value}`.includes('<a')
                        ? removeNbsp(extractedValue)
                        : `${value}`.replace(/\./g, ',');
                }
            });
        }

        if (key === 'children' && value && value.length > 0) {
            if (value[0].group !== 'empty') {
                value.forEach((childItem: TableDataItem) => {
                    const childRows = parseRow(childItem, metrics, row[0]);
                    
                    let strRow = '\n';
                    childRows.forEach((tempItem) => {
                        tempItem.forEach((item3: any) => {
                            strRow += `${removeNbsp(item3)}\t`;
                        });
                    });
                    row.push(strRow);
                });
            }
        }
    });

    return [row];
};

/**
 * Генерирует заголовок таблицы для копирования
 */
const generateHeader = (groupings: GroupingItem[], metrics: MetricItem[]): string => {
    let headerRowStr = '';

    groupings.forEach((groupItem, index) => {
        if (index === 0) {
            headerRowStr += groupItem.label || '';
        } else {
            headerRowStr += ` - ${groupItem.label || ''}`;
        }
    });

    headerRowStr += '\t';

    metrics.forEach((metricItem) => {
        headerRowStr += `${metricItem.label || ''}\t`;
    });

    return headerRowStr;
};

/**
 * Парсит все данные таблицы
 */
const parseTableData = (data: TableDataItem[], metrics: MetricItem[]): string => {
    let copyDataStr = '';

    data.forEach((item) => {
        const [row] = parseRow(item, metrics);
        let strRow = '';
        row.forEach((cellValue: string) => {
            strRow += `${removeNbsp(cellValue)}\t`;
        });
        strRow += '\n';
        copyDataStr += strRow;
    });

    return copyDataStr;
};

/**
 * Фильтрует данные, оставляя только строки последнего уровня
 */
const filterLastLevelData = (rawData: string): string => {
    const lines = rawData.split('\n');
    const filteredLines = lines.filter((line) => {
        const prefix = line.split('\t')[0];
        return !lines.some(
            (otherLine) =>
                otherLine !== line &&
                otherLine.startsWith(prefix) &&
                otherLine.length > line.length
        );
    });
    return filteredLines.join('\n');
};

/**
 * Копирует данные в буфер обмена с показом уведомления
 */
const copyToClipboard = async (text: string): Promise<void> => {
    const success = await safeCopyToClipboard(text);
    if (success) {
        message.success({
            content: 'Скопировано',
            style: { marginTop: '20%' },
        });
    } else {
        message.error({
            content: 'Ошибка при копировании данных',
            style: { marginTop: '20%' },
        });
    }
};

/**
 * Текст всех уровней таблицы для вставки в Excel (заголовок + строки) — без копирования
 */
export const buildAllTableDataText = (
    data: TableDataItem[],
    groupings: GroupingItem[],
    metrics: MetricItem[]
): string => `${generateHeader(groupings, metrics)}\n${parseTableData(data, metrics)}`;

/**
 * Уведомление о результате копирования (как у copyAllTableData)
 */
export const showCopyResult = (success: boolean): void => {
    if (success) {
        message.success({ content: 'Скопировано', style: { marginTop: '20%' } });
    } else {
        message.error({ content: 'Ошибка при копировании данных', style: { marginTop: '20%' } });
    }
};

/**
 * Копирует все уровни данных таблицы
 */
export const copyAllTableData = (
    data: TableDataItem[],
    groupings: GroupingItem[],
    metrics: MetricItem[]
): void => {
    try {
        const header = generateHeader(groupings, metrics);
        const tableData = parseTableData(data, metrics);
        const copyDataStr = `${header}\n${tableData}`;
        copyToClipboard(copyDataStr);
    } catch {
        message.error({
            content: 'Ошибка при копировании данных',
            style: { marginTop: '20%' },
        });
    }
};

/**
 * Копирует только последний уровень данных таблицы
 */
export const copyLastLevelTableData = (
    data: TableDataItem[],
    tableDataRows: TableDataItem[],
    groupings: GroupingItem[],
    metrics: MetricItem[]
): void => {
    // Если есть дочерние элементы, фильтруем до последнего уровня
    if (tableDataRows.some((el) => el.children && el.children.length > 0)) {
        try {
            const header = generateHeader(groupings, metrics);
            const tableData = parseTableData(data, metrics);
            const filteredData = filterLastLevelData(tableData);
            const copyDataStr = `${header}\n${filteredData}`;
            copyToClipboard(copyDataStr);
        } catch {
            message.error({
                content: 'Ошибка при копировании данных',
                style: { marginTop: '20%' },
            });
        }
    } else {
        // Если нет дочерних элементов, копируем все данные
        copyAllTableData(data, groupings, metrics);
    }
};
